/**
 * Generic message utility for displaying Bootstrap alerts
 * @param {Object} options - Configuration options for the alert
 * @param {string} options.message - The message content (HTML allowed) - REQUIRED
 * @param {string} [options.type='info'] - Alert type (primary, secondary, success, danger, warning, info, light, dark)
 * @param {boolean} [options.dismissible=true] - Whether the alert can be manually dismissed
 * @param {number|null} [options.autoDismiss=null] - Auto dismiss after X seconds (null = no auto dismiss)
 * @param {string} [options.containerSelector='body'] - Container selector to append the alert
 * @param {boolean} [options.clearContainer=false] - Whether to clear existing alerts before adding new one
 * @param {string} [options.customClass=''] - Additional CSS classes to add to the alert
 * @param {boolean} [options.arrow=false] - Whether to add arrow styling (requires alert-arrow class)
 * @param {string} [options.arrowPosition='top'] - Arrow position (top, bottom, left, right)
 * @returns {HTMLElement|null} - The created alert element or null if failed
 */
function showMessage(options) {
    // Validate required parameters
    if (!options || !options.message) {
        // eslint-disable-next-line no-console
        console.error('Message utility: message is required');
        return null;
    }

    // Type aliases mapping
    const typeAliases = {
        error: 'danger',
    };

    // Default options
    const defaults = {
        type: 'info',
        dismissible: true,
        autoDismiss: null,
        clearContainer: false,
        containerSelector: 'body',
        customClass: '',
        arrow: false,
        arrowPosition: 'top',
    };

    // Merge options with defaults
    const config = { ...defaults, ...options };

    // Apply type aliases
    const alertType = typeAliases[config.type] || config.type;

    // Validate alert type
    const validTypes = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];
    if (!validTypes.includes(alertType)) {
        // eslint-disable-next-line no-console
        console.warn(`Message utility: Invalid alert type "${config.type}". Using "info" instead.`);
        config.type = 'info';
    } else {
        config.type = alertType;
    }

    // Find container
    const container = document.querySelector(config.containerSelector);
    if (!container) {
        // eslint-disable-next-line no-console
        console.error(`Message utility: Container "${config.containerSelector}" not found`);
        return null;
    }

    // Clear container if requested
    if (config.clearContainer) {
        container.innerHTML = '';
    }

    // Build CSS classes
    const cssClasses = ['alert', `alert-${config.type}`];

    if (config.dismissible) {
        cssClasses.push('alert-dismissible', 'fade', 'show');
    }

    if (config.arrow) {
        cssClasses.push('alert-arrow');
        if (config.arrowPosition && config.arrowPosition !== 'top') {
            cssClasses.push(`alert-arrow-${config.arrowPosition}`);
        }
    }

    if (config.customClass) {
        cssClasses.push(config.customClass);
    }

    // Create alert element
    const alertElement = document.createElement('div');
    alertElement.className = cssClasses.join(' ');
    alertElement.setAttribute('role', 'alert');

    // Set message content
    alertElement.innerHTML = config.message;

    // Add dismiss button if dismissible
    if (config.dismissible) {
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'close';
        closeButton.setAttribute('data-dismiss', 'alert');
        closeButton.setAttribute('aria-label', 'Close');

        const closeIcon = document.createElement('span');
        closeIcon.setAttribute('aria-hidden', 'true');
        closeIcon.innerHTML = '&times;';

        closeButton.appendChild(closeIcon);
        alertElement.appendChild(closeButton);
    }

    // Append to container
    container.appendChild(alertElement);

    // Handle auto dismiss
    if (config.autoDismiss && typeof config.autoDismiss === 'number' && config.autoDismiss > 0) {
        setTimeout(() => {
            if (alertElement.parentNode) {
                // Use Bootstrap's alert dismiss if available, otherwise remove manually
                if (window.$ && window.$.fn.alert) {
                    $(alertElement).alert('close');
                } else {
                    alertElement.remove();
                }
            }
        }, config.autoDismiss * 1000);
    }

    return alertElement;
}

/**
 * Convenience function for showing error messages
 * @param {string} message - The error message
 * @param {string} containerSelector - Container selector to append the alert
 * @param {Object} [additionalOptions={}] - Additional options to override defaults
 * @returns {HTMLElement|null} - The created alert element or null if failed
 */
function showErrorMessage(message, containerSelector, additionalOptions = {}) {
    const errorOptions = {
        message,
        containerSelector,
        type: 'danger',
        ...additionalOptions,
    };

    return showMessage(errorOptions);
}

/**
 * Convenience function for showing success messages
 * @param {string} message - The success message
 * @param {string} containerSelector - Container selector to append the alert
 * @param {Object} [additionalOptions={}] - Additional options to override defaults
 * @returns {HTMLElement|null} - The created alert element or null if failed
 */
function showSuccessMessage(message, containerSelector, additionalOptions = {}) {
    const successOptions = {
        message,
        containerSelector,
        type: 'success',
        ...additionalOptions,
    };

    return showMessage(successOptions);
}

/**
 * Clear all messages from a container
 * @param {string} containerSelector - Container selector to clear
 */
function clearMessages(containerSelector) {
    const container = document.querySelector(containerSelector);
    if (container) {
        container.innerHTML = '';
    }
}

module.exports = {
    showMessage,
    showErrorMessage,
    showSuccessMessage,
    clearMessages,
};
